import {
  Box,
  Container,
  ModalContent,
  ModalCloseButton,
  ModalFooter,
  SimpleGrid,
  GridItem,
  useDisclosure,
} from '@chakra-ui/react';
import { format } from 'date-fns';
import { Formik, Form } from 'formik';
import React, { useCallback, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import Button from 'components/Button';
import { FormikDateRangePicker, FormikSelectAsyncPaginate } from 'components/Formik';
import { ControllableModal } from 'components/Modal';
import Title from 'components/Title';
import { useFetchReportsByTask } from 'hooks/reports/useFetchReportsByTask';
import { fetchPaginatedTasks } from 'hooks/task/useFetchPaginatedTasks';
import { getAsyncSelectLoadOptions } from 'utils/helpers';

import ReportsByJobFilterTable from './ReportsByTaskFilterTable';
import ReportsByTaskTable from './ReportsByTaskTable';
import { initialReportsByJobFormData, reportsByJobValidationSchema } from './schema';

const ReportsByTask: React.FC<unknown> = () => {
  const { t } = useTranslation(['common', 'reports']);
  const formikRef = useRef<any>();

  const [checkedTimesheet, setCheckedTimesheet] = useState<string[]>([]);
  const [reports, setReportsByJob] = useState(null);
  const [filteredReportsByJob, setFilteredReportsByJob] = useState(null);

  const { isOpen, onOpen, onClose } = useDisclosure();
  const { status, mutateAsync } = useFetchReportsByTask();

  const taskLoadOptions = (page: number, search: string) => {
    return getAsyncSelectLoadOptions(fetchPaginatedTasks, { page, search, task_status: 1 });
  };

  const handleCheckboxChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>, timesheetId: string) => {
      if (!e.target.checked) {
        const updatedTimesheet = checkedTimesheet.filter((item: string) => item !== timesheetId);
        setCheckedTimesheet(updatedTimesheet);
      } else {
        const updatedTimesheet = [...checkedTimesheet, timesheetId];
        setCheckedTimesheet(updatedTimesheet);
      }
    },
    [checkedTimesheet]
  );

  const handleFilterButtonClick = useCallback(() => {
    const postData = {
      start_date: formikRef.current ? format(formikRef.current.values.date_range[0], 'MM-dd-yyyy') : '',
      end_date: formikRef.current ? format(formikRef.current.values.date_range[1], 'MM-dd-yyyy') : '',
      task_id: formikRef.current?.values?.task_id ? formikRef.current.values.task_id?.id : '',
      type: 'byTask',
      timesheet_ids: checkedTimesheet,
    };

    mutateAsync(postData).then(data => {
      setFilteredReportsByJob(data?.payload);
      onOpen();
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkedTimesheet, mutateAsync]);

  const handleFormSubmit = (values: typeof initialReportsByJobFormData) => {
    const postData = {
      start_date: values.date_range[0] !== null ? format(values.date_range[0], 'MM-dd-yyyy') : '',
      end_date: values.date_range[1] !== null ? format(values.date_range[1], 'MM-dd-yyyy') : '',
      task_id: values.task_id ? values.task_id?.id : '',
      type: 'byTask',
    };

    mutateAsync(postData).then(data => {
      setFilteredReportsByJob(null);
      setCheckedTimesheet([]);
      setReportsByJob(data?.payload);
    });
  };

  return (
    <div>
      <Title>{t('reports:reportByTask')}</Title>
      <Container mt="5" maxW="container.sm" p="0">
        <Formik
          initialValues={initialReportsByJobFormData}
          validationSchema={reportsByJobValidationSchema}
          onSubmit={handleFormSubmit}
        >
          {formikBag => {
            formikRef.current = formikBag;
            return (
              <Form autoComplete="off">
                <SimpleGrid columns={{ base: 1, sm: 2 }} gap={{ base: 0, sm: 3 }}>
                  <GridItem>
                    <FormikSelectAsyncPaginate
                      name="task_id"
                      label={t('common:taskName')}
                      loadOptions={(search: any, _: any, { page }: any) => {
                        return taskLoadOptions(page, search);
                      }}
                      selectProps={{
                        getOptionLabel: (option: any) => option.name,
                        getOptionValue: (option: any) => option.id,
                        placeholder: t('common:selectTask'),
                      }}
                    />
                  </GridItem>
                  <GridItem>
                    <FormikDateRangePicker
                      label={t('common:dateRange')}
                      name="date_range"
                      // datePickerProps={{
                      //   maxDate: addMonthsToDate(new Date(values.date_range[0] ?? ''), 2),
                      // }}
                    />
                  </GridItem>
                </SimpleGrid>
                <Box textAlign="right">
                  <Button type="submit" colorScheme="secondary" isLoading={status === 'loading'}>
                    {t('reports:generateReport')}
                  </Button>
                </Box>
              </Form>
            );
          }}
        </Formik>
      </Container>
      {reports ? (
        <ReportsByTaskTable
          data={reports}
          checkedTimesheet={checkedTimesheet}
          dateRange={formikRef?.current?.values?.date_range}
          handleCheckboxChange={handleCheckboxChange}
        >
          {checkedTimesheet.length ? (
            <Button colorScheme="primary" mt="4" onClick={handleFilterButtonClick} isLoading={status === 'loading'}>
              {t('common:filter')}
            </Button>
          ) : null}
        </ReportsByTaskTable>
      ) : null}

      <ControllableModal size="full" isOpen={isOpen} onClose={onClose} scrollBehavior="inside">
        <ModalContent overflowY="auto">
          <ModalCloseButton />
          <ReportsByJobFilterTable data={filteredReportsByJob} dateRange={formikRef?.current?.values?.date_range} />
          <ModalFooter>
            <Button onClick={onClose} w="25%">
              {t('common:close')}
            </Button>
          </ModalFooter>
        </ModalContent>
      </ControllableModal>
    </div>
  );
};

export default ReportsByTask;
